import { ElementRef, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { SessionService } from '@app/core/services/session/session.service';
import { FilterComponent } from '@app/features/filter/widgets/filter/filter.component';
import { FieldType } from '@app/shared/constants';
import { FilterItem, FilterOutput, FiltersOutput } from '@app/shared/models/filter.interface';
import { RSQLOperator } from '@app/shared/models/rsql/rsql.operators';
import { SearchFilter } from '@components/data/data-table-new/directives/datatable-search.directive';
import { Column } from '@components/data/data-table/data-table.component';
import { Observable } from 'rxjs';
import { SanitizerService } from '../sanitizer/sanitizer.service';

/**
 * The filter state. Just a definition to be used in the modules to
 * restore the values when the filter is shown.
 */
export interface FilterState {
  search: string;
  value: { [key: string]: FilterOutput };
}

/** The FilterState implementation */
export class FilterStateImpl implements FilterState {
  search = '';
  value = {};
}

@Injectable({
  providedIn: 'root'
})
export class FilterService {
  static FILTER_TOKEN_SEPARATOR = ',';
  static FILTER_AND = ' and ';
  static FILTER_OR = ' or ';

  formGroup: FormGroup;

  /**
   * Constructor
   *
   * @param dialog MatDialog instance
   * @param sanitizeService sanitizer service
   */
  constructor(
    public dialog: MatDialog,
    protected sanitizeService: SanitizerService,
    protected sessionService: SessionService
  ) {}

  /**
   * Open with an ElementRef instance
   *
   * @param ref ElementRef instance
   * @return an observable to wait until the user closes the dialog
   */
  openWithTemplate(ref: ElementRef): Observable<boolean> {
    return this.dialog
      .open(FilterComponent, {
        width: '250px',
        panelClass: 'vfs-filter',
        data: { content: ref }
      })
      .afterClosed();
  }

  /**
   * Open with a list of filter elements
   *
   * @param filters array of filter elements
   * @return an observable to wait until the user closes the dialog
   */
  openWithData(filters: FilterItem[]): Observable<FiltersOutput> {
    return this.dialog
      .open(FilterComponent, {
        width: '250px',
        panelClass: 'vfs-filter',
        data: { content: filters }
      })
      .afterClosed();
  }

  /**
   * Build a filter for a group of columns. This is usually done in the data table
   * component (search box)
   *
   * @param value value to filter
   * @param columns columns
   * @param filterState filterState (optional)
   * @return the filter as expected by the BackEnd
   */
  buildFilterForSearch(value: string, columns: Column[], filterState: FilterState): string {
    let sanitized = this.sanitizeService.sanitizeFilter(value);
    let filters = '';
    if (value.trim() !== '') {
      columns.forEach(column => {
        if (column.searchable) {
          if (filters !== '') {
            filters += FilterService.FILTER_OR;
          }
          filters += this.getTokenToServer(sanitized, column.name, column.fieldType);
        }
      });

      if (filters !== '') {
        filters = '(' + filters + ')';
      }

      if (filterState) {
        let stateFilter = this.getStateFilters(filterState);
        if (stateFilter !== '') {
          filters += FilterService.FILTER_AND + stateFilter;
        }
      }
    } else {
      filters += this.getStateFilters(filterState);
    }
    return filters;
  }

  /**
   * FIXME: Defined with the intent to deprecate "buildFilterForSearch".
   */
  buildFilterForSearchNew(filterState: FilterState, columns: SearchFilter[]): string {
    const value: string = filterState.search;
    let sanitized = this.sanitizeService.sanitizeFilter(value);
    let filters = '';
    if (value.trim() !== '') {
      columns.forEach(column => {
        if (filters !== '') {
          filters += FilterService.FILTER_OR;
        }
        filters += this.getTokenToServer(sanitized, column.name, column.type);
      });

      if (filters !== '') {
        filters = '(' + filters + ')';
      }

      if (filterState) {
        let stateFilter = this.getStateFilters(filterState);
        if (stateFilter !== '') {
          filters += FilterService.FILTER_AND + stateFilter;
        }
      }
    } else {
      filters += this.getStateFilters(filterState);
    }
    return filters;
  }

  /**
   * Convert and array of FilterOutput items to an string compatible
   * with RSQL specification
   *
   * @param filters the filters
   * @return the string described above
   */
  convertFilters(filters: FilterOutput[], operator = FilterService.FILTER_AND): string {
    let appliedFilters = '';
    let filterNum = 0;
    filters.forEach((f: FilterOutput) => {
      if (f.value !== undefined && f.value != null) {
        if (f.value instanceof Array && f.operator !== RSQLOperator.IN_HIERARCHY) {
          if (f.value.length > 0) {
            if (filterNum > 0) {
              appliedFilters += operator;
            }
            appliedFilters += '(';
            f.value.forEach((value, index) => {
              if (index > 0) {
                appliedFilters += FilterService.FILTER_OR;
              }
              appliedFilters += this.getTokenToServer(
                this.sanitizeService.sanitizeFilter(value),
                f.key,
                f.valueType,
                f.expression,
                f.operator
              );
            });
            appliedFilters += ')';
            filterNum++;
          }
        } else if (f.operator === RSQLOperator.IN_HIERARCHY) {
          if (f.value.length === 5 && f.value[1] && f.value[2]) {
            if (filterNum > 0) {
              appliedFilters += operator;
            }
            appliedFilters +=
              '(' +
              this.getTokenToServer(
                `(${(f.value as string[]).join(',')})`,
                f.key,
                f.valueType,
                f.expression,
                f.operator
              ) +
              ')';
            filterNum++;
          }
        } else if (f.value !== '') {
          if (filterNum > 0) {
            appliedFilters += operator;
          }
          appliedFilters +=
            '(' +
            this.getTokenToServer(
              this.sanitizeService.sanitizeFilter(f.value + ''),
              f.key,
              f.valueType,
              f.expression,
              f.operator
            ) +
            ')';
          filterNum++;
        }
      }
    });
    return appliedFilters;
  }

  /**
   * Get the filters based on the state
   *
   * @param filterState state
   * @return the filters to apply
   */
  public getStateFilters(filterState: FilterState, operator = FilterService.FILTER_AND): string {
    let filters: FilterOutput[] = [];
    if (filterState) {
      for (let key in filterState.value) {
        if ((filterState.value[key].value && !filterState.value[key].disabled) || filterState.value[key].value === 0) {
          filters.push(filterState.value[key]);
        }
      }
    }
    return this.convertFilters(filters, operator);
  }

  /**
   * Get the applied filters based on the filters contents as expected
   * by the BackEnd. This method usally will be used when the FilterComponent
   * is being used.
   *
   * @param filtersOutput the filters
   * @return the filters to apply
   */
  getAppliedFilters(filtersOutput: FiltersOutput): string {
    let appliedFilters = '';
    if (filtersOutput && filtersOutput.filters) {
      appliedFilters = this.convertFilters(filtersOutput.filters);
    }
    return appliedFilters;
  }

  /**
   * Get a token as expected by the server
   *
   * @see https://github.com/jirutka/rsql-parser
   *
   * @param value sanetized value
   * @param name the column name
   * @param fieldType the field type
   * @return the filter
   */
  protected getTokenToServer(
    value: string,
    name: string,
    fieldType: string,
    expression = '',
    operator: RSQLOperator = RSQLOperator.EQUAL
  ) {
    let token = '';
    if (expression !== '') {
      return expression.replace('${LOGGEDINUSER}', this.sessionService.getUser().id);
    }
    switch (fieldType) {
      case FieldType.NUMBER:
      case FieldType.BOOLEAN:
      case FieldType.UUID:
        token = name + operator + value;
        break;
      case FieldType.DATE:
        token = name + operator + '"' + value + '"';
        break;

      case FieldType.HIERARCHY:
        token = name + operator + value;
        break;
      case FieldType.STRING:
      case FieldType.CUSTOM:
      default:
        token += name + operator + '"*' + value + '*"';
        break;
    }
    return token;
  }
}
